import { createResource, For, Match, Switch } from 'solid-js'
import { authMe } from '../store/auth.ts'
import { trpc } from '../trpc.ts'
import { Button } from './ui/Button.tsx'

/*
  "let this mcp client in?" - where /api/oauth/authorize sends a signed-in person. the
  details and the signed consent token come from the backend; the answer goes back as
  a plain form post, not through trpc, because what has to happen next is a top-level
  navigation to the client's redirect_uri and only the browser can do that one
*/
export function OAuthConsentPage() {
  const params = Object.fromEntries(new URLSearchParams(location.search))
  const [consent] = createResource(() => params.error ? null : trpc.oauth.consent.query(params))

  const error = () => params.error ?? (consent.error as Error | undefined)?.message

  return (
    <div class="h-full flex flex-col items-center justify-center gap-6 px-4">
      <div class="flex items-center gap-2">
        <div class="i-lucide-search-code size-5 text-muted-foreground" />
        <span class="text-sm font-bold">codesearch</span>
      </div>

      <div class="max-w-md w-full flex flex-col gap-4 border rounded-lg bg-background p-6 shadow-sm">
        <Switch>
          <Match when={error()}>
            <div class="flex items-center gap-2 font-medium">
              <div class="i-lucide-circle-alert size-4 text-destructive" />
              can't connect
            </div>
            <div class="text-sm text-muted-foreground">{error()}</div>
          </Match>

          <Match when={consent()}>
            {data => (
              <>
                <div class="flex items-center gap-2 font-medium">
                  <div class="i-lucide-plug size-4 text-muted-foreground" />
                  connect
                  {' '}
                  <code class="rounded bg-muted px-1.5 py-0.5 text-sm">{data().clientName}</code>
                  ?
                </div>

                <div class="flex flex-col gap-2 text-sm text-muted-foreground">
                  <p>
                    it will be able to search and read every index
                    {' '}
                    <span class="text-foreground font-medium">{authMe()?.displayName}</span>
                    {' '}
                    can see.
                  </p>
                  <p>
                    the token goes to
                    {' '}
                    <code class="break-all rounded bg-muted px-1 text-foreground">{data().redirectUri}</code>
                  </p>
                  <p>if you didn't just add codesearch to an mcp client, deny.</p>
                </div>

                <form method="post" action="/api/oauth/authorize" class="flex justify-end gap-2 pt-2">
                  <For each={Object.entries(data().fields)}>
                    {([name, value]) => <input type="hidden" name={name} value={value} />}
                  </For>
                  <Button type="submit" name="decision" value="deny" variant="outline">deny</Button>
                  <Button type="submit" name="decision" value="approve" class="gap-2">
                    <div class="i-lucide-check size-4" />
                    allow
                  </Button>
                </form>
              </>
            )}
          </Match>

          <Match when={consent.loading}>
            <div class="flex items-center gap-2 text-sm text-muted-foreground">
              <div class="i-lucide-loader-circle size-4 animate-spin" />
              checking the request
            </div>
          </Match>
        </Switch>
      </div>
    </div>
  )
}
